import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, RefreshCw } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { toast } from "sonner";
import { StatCard, StatusBadge, fmtUsd, fmtDate } from "./userDetail/shared";
import { UserProfileForm } from "./userDetail/UserProfileForm";
import { UserPricingForm } from "./userDetail/UserPricingForm";
import { UserWalletTab } from "./userDetail/UserWalletTab";
import { UserAdAccountsTab } from "./userDetail/UserAdAccountsTab";
import { UserTransactionsTab, UserSecurityCard, UserNotificationsList } from "./userDetail/UserActivityTabs";

const TABS = [
    ["profile", "Profil"], ["pricing", "Fiyatlandırma"], ["wallet", "Cüzdan & Yatırımlar"],
    ["accounts", "Reklam Hesapları"], ["transactions", "İşlemler"], ["activity", "Bildirimler & Güvenlik"],
];

export default function AdminUserDetail() {
    const { id } = useParams();
    const [data, setData] = useState(null);
    const [error, setError] = useState("");
    const [version, setVersion] = useState(0);

    const load = useCallback(async () => {
        try {
            const res = await api.get(`/admin/users/${id}/detail`);
            setData(res.data);
            setError("");
        } catch (err) {
            setError(formatApiErrorDetail(err?.response?.data?.detail) || "Kullanıcı yüklenemedi");
        }
    }, [id]);
    useEffect(() => { load(); }, [load]);
    const refresh = () => { load(); setVersion((v) => v + 1); };

    if (error) {
        return (
            <div data-testid="admin-user-detail-error" className="space-y-4">
                <Link to="/admin/users" className="text-sm text-primary inline-flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Kullanıcılar</Link>
                <p className="text-red-300">{error}</p>
            </div>
        );
    }
    if (!data) return <p className="text-sm text-muted-foreground" data-testid="admin-user-detail-loading">Yükleniyor…</p>;

    const { user, wallet, policy, stats, deposit_addresses, ad_accounts, topups, transactions, deposits, notifications, application } = data;

    return (
        <div data-testid="admin-user-detail" className="space-y-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                    <Link to="/admin/users" data-testid="user-detail-back" className="text-xs text-muted-foreground hover:text-primary inline-flex items-center gap-1 mb-2"><ArrowLeft className="w-3.5 h-3.5" /> Kullanıcılar</Link>
                    <h1 className="font-display font-bold text-3xl tracking-tight" data-testid="user-detail-name">{user.full_name}</h1>
                    <div className="flex flex-wrap items-center gap-2 mt-1.5 text-xs">
                        <span className="font-mono text-muted-foreground" data-testid="user-detail-email">{user.email}</span>
                        <StatusBadge value={user.status} testid="user-detail-status" />
                        <StatusBadge value={user.role} testid="user-detail-role" />
                        <span className="text-muted-foreground">Kayıt: {fmtDate(user.created_at)}</span>
                        {application && <span className="text-muted-foreground">· Başvuru: {application.status}</span>}
                    </div>
                    {user.admin_note && <p className="mt-2 text-xs text-amber-200/90 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-1.5 inline-block" data-testid="user-detail-admin-note">{user.admin_note}</p>}
                </div>
                <button type="button" onClick={refresh} data-testid="user-detail-refresh" className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg border border-white/10 hover:bg-white/[0.06]"><RefreshCw className="w-3.5 h-3.5" /> Yenile</button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
                <StatCard label="Cüzdan" value={fmtUsd(wallet.usd_balance)} accent="text-emerald-300" testid="stat-balance" />
                <StatCard label="Cashback" value={fmtUsd(wallet.cashback_balance)} testid="stat-cashback" />
                <StatCard label="Toplam yatırım" value={fmtUsd(stats.total_deposited_usd)} sub={`${stats.deposits_credited} yatırım`} testid="stat-deposited" />
                <StatCard label="Ödenen komisyon" value={fmtUsd(stats.total_fees_usd)} testid="stat-fees" />
                <StatCard label="Reklam hesabı" value={`${stats.ad_accounts_active}/${stats.ad_accounts_total}`} sub={`${stats.ad_accounts_pending} bekliyor · ${stats.topups_total} ek yükleme`} testid="stat-accounts" />
                <StatCard label="Kademe" value={policy.tier} sub={`W ${policy.fee_by_vertical.whiteHat}% · G ${policy.fee_by_vertical.grayHat}% · C ${policy.fee_by_vertical.casino}%`} testid="stat-tier" />
            </div>

            <Tabs defaultValue="profile">
                <TabsList className="flex flex-wrap h-auto gap-1 bg-card border border-white/[0.07] p-1">
                    {TABS.map(([v, label]) => <TabsTrigger key={v} value={v} data-testid={`user-tab-${v}`} className="data-[state=active]:bg-primary data-[state=active]:text-white">{label}</TabsTrigger>)}
                </TabsList>
                <TabsContent value="profile" className="mt-4"><UserProfileForm key={`p${version}`} user={user} onSaved={refresh} /></TabsContent>
                <TabsContent value="pricing" className="mt-4"><UserPricingForm key={`f${version}`} user={user} policy={policy} onSaved={refresh} /></TabsContent>
                <TabsContent value="wallet" className="mt-4"><UserWalletTab user={user} wallet={wallet} addresses={deposit_addresses} deposits={deposits} onChanged={refresh} /></TabsContent>
                <TabsContent value="accounts" className="mt-4"><UserAdAccountsTab accounts={ad_accounts} topups={topups} onChanged={refresh} /></TabsContent>
                <TabsContent value="transactions" className="mt-4"><UserTransactionsTab transactions={transactions} /></TabsContent>
                <TabsContent value="activity" className="mt-4 space-y-4"><UserSecurityCard user={user} /><UserNotificationsList notifications={notifications} /></TabsContent>
            </Tabs>
        </div>
    );
}
